/* Price filtering and sorting follow displayed unit prices, not checkout box prices. */
const WfsUnitPriceFilter = (() => {
  const minKey = 'filter.v.price.gte';
  const maxKey = 'filter.v.price.lte';
  const cache = new Map();
  const parse = html => new DOMParser().parseFromString(html, 'text/html');
  const note = 'Flooring: price per m². Accessories: item price.';

  function bounds(url) {
    function cents(key) {
      const value = url.searchParams.get(key);
      if (value === null || value.trim() === '') return null;
      const number = Number(value.replace(',', '.'));
      return Number.isFinite(number) && number >= 0 ? Math.round(number * 100) : null;
    }
    return { min: cents(minKey), max: cents(maxKey) };
  }

  function sortDirection(url) {
    const sort = url.searchParams.get('sort_by');
    return sort === 'price-ascending' ? 1 : sort === 'price-descending' ? -1 : 0;
  }

  async function read(url) {
    const response = await fetch(url.toString());
    if (!response.ok) throw new Error('Could not load products for the price filter.');
    const html = await response.text();
    const doc = parse(html);
    if (!doc.getElementById('ProductGridContainer')) throw new Error('Invalid product-grid response.');
    return doc;
  }

  function pages(doc, url) {
    let count = Number(doc.querySelector('#CollectionLoadMore')?.dataset.totalPages) || 1;
    doc.querySelectorAll('.pagination a[href]').forEach(link => {
      const page = Number(new URL(link.getAttribute('href'), url).searchParams.get('page'));
      if (Number.isInteger(page)) count = Math.max(count, page);
    });
    return count;
  }

  async function catalog(url) {
    const key = url.toString();
    const stored = cache.get(key);
    if (stored && Date.now() - stored.time < 60000) return stored.promise;
    const promise = (async () => {
      const first = await read(url);
      const total = pages(first, url);
      const docs = [first];
      // Limit concurrency, never limit which pages participate in the result.
      for (let start = 2; start <= total; start += 3) {
        const batch = [];
        for (let page = start; page <= total && page < start + 3; page++) {
          const next = new URL(url);
          next.searchParams.set('page', String(page));
          batch.push(read(next));
        }
        docs.push(...await Promise.all(batch));
      }
      return docs.map(doc => doc.documentElement.outerHTML);
    })();
    cache.set(key, { time: Date.now(), promise });
    if (cache.size > 6) cache.delete(cache.keys().next().value);
    try {
      return await promise;
    } catch (error) {
      cache.delete(key);
      throw error;
    }
  }

  function syncInputs(doc, range, maximum) {
    const upper = Math.max(1, Math.ceil(maximum / 100), (range.min || 0) / 100, (range.max || 0) / 100);
    doc.querySelectorAll(`input[name="${minKey}"],input[name="${maxKey}"]`).forEach(input => {
      const value = input.name === minKey ? range.min : range.max;
      input.setAttribute('value', value === null ? '' : String(value / 100));
      input.setAttribute('data-max', String(upper));
      input.setAttribute('data-min', '0');
      input.setAttribute('placeholder', input.name === minKey ? '0' : String(upper));
    });
    doc.querySelectorAll('.facets-drawer-price .range-group').forEach(group => {
      group.removeAttribute('data-range-init');
      const low = group.querySelector('.range-slider--min');
      const high = group.querySelector('.range-slider--max');
      if (!low || !high) return;
      for (const slider of [low, high]) slider.setAttribute('max', String(upper));
      low.setAttribute('value', String((range.min || 0) / 100));
      high.setAttribute('value', String(range.max === null ? upper : range.max / 100));
      group.style.setProperty('--range-min', `${Number(low.getAttribute('value')) / upper * 100}%`);
      group.style.setProperty('--range-max', `${Number(high.getAttribute('value')) / upper * 100}%`);
    });
  }

  function syncLinks(doc, original, range) {
    doc.querySelectorAll('facet-remove a[href]').forEach(link => {
      const isClearAll = link.classList.contains('active-facets__button-remove') ||
        /clear all|remove all/i.test(link.textContent);
      const priceDetail = link.closest('.js-filter')?.querySelector(`input[name="${minKey}"]`);
      if (isClearAll || priceDetail) return;
      const target = new URL(link.getAttribute('href'), original);
      for (const name of [minKey, maxKey]) {
        if (original.searchParams.has(name)) target.searchParams.set(name, original.searchParams.get(name));
      }
      link.setAttribute('href', target.pathname + target.search);
    });
    const cleared = new URL(original);
    cleared.searchParams.delete(minKey);
    cleared.searchParams.delete(maxKey);
    cleared.searchParams.delete('section_id');
    cleared.searchParams.delete('page');
    const format = value => new Intl.NumberFormat(document.documentElement.lang || 'en-IE', {
      style: 'currency', currency: window.Shopify?.currency?.active || 'EUR',
    }).format(value / 100);
    const label = range.min === null ? `Price: up to ${format(range.max)}` :
      range.max === null ? `Price: from ${format(range.min)}` :
      `Price: ${format(range.min)} – ${format(range.max)}`;
    doc.querySelectorAll('.active-facets-mobile,.active-facets-desktop').forEach(container => {
      const remove = doc.createElement('facet-remove');
      const link = doc.createElement('a');
      link.className = 'active-facets__button active-facets__button--light';
      link.href = cleared.pathname + cleared.search;
      link.setAttribute('aria-label', `Remove ${label} filter`);
      const inner = doc.createElement('span');
      inner.className = 'active-facets__button-inner';
      inner.textContent = `${label} ×`;
      link.append(inner);
      remove.append(link);
      container.append(remove);
    });
  }

  async function fetchSection(value) {
    const original = new URL(value, window.location.origin);
    const range = bounds(original);
    const hasRange = range.min !== null || range.max !== null;
    const direction = sortDirection(original);
    if (!hasRange && !direction) {
      const doc = await read(original);
      return doc.documentElement.outerHTML;
    }
    const source = new URL(original);
    source.searchParams.delete(minKey);
    source.searchParams.delete(maxKey);
    source.searchParams.delete('page');
    const documents = (await catalog(source)).map(parse);
    const doc = documents[0];
    const grid = doc.getElementById('product-grid');
    if (!grid) {
      syncInputs(doc, range, 0);
      if (hasRange) syncLinks(doc, original, range);
      return doc.documentElement.outerHTML;
    }
    const all = [];
    const otherResults = [];
    const seen = new Set();
    documents.forEach(page => {
      page.querySelectorAll('#product-grid > li').forEach(item => {
        const card = item.querySelector('[data-wfs-filter-price]');
        if (!card) {
          if (item.querySelector('.product-card-wrapper')) throw new Error('Product price metadata is unavailable.');
          if (!hasRange && item.querySelector('.article-card-wrapper')) otherResults.push(item);
          return;
        }
        const price = Number(card.dataset.wfsFilterPrice);
        if (!Number.isFinite(price)) throw new Error('A product has no valid filter price.');
        const id = card.dataset.productId;
        if (seen.has(id)) return;
        seen.add(id);
        all.push({ item, price });
      });
    });
    const matches = all.filter(({ price }) => (range.min === null || price >= range.min) &&
      (range.max === null || price <= range.max));
    if (direction) matches.sort((a, b) => direction * (a.price - b.price));
    grid.replaceChildren(
      ...matches.map(({ item }) => doc.importNode(item, true)),
      ...otherResults.map(item => doc.importNode(item, true))
    );
    doc.querySelectorAll('.clm-wrapper,.pagination-wrapper').forEach(element => element.remove());
    const count = hasRange ? `${matches.length} of ${all.length} products` :
      `${matches.length + otherResults.length} ${otherResults.length ? 'results' : 'products'}`;
    doc.querySelectorAll('#ProductCount,#ProductCountDesktop,.mobile-facets__count').forEach(element => {
      element.textContent = count;
    });
    if (!matches.length && !otherResults.length) {
      const empty = doc.createElement('p');
      empty.className = 'title title--primary';
      empty.setAttribute('role', 'status');
      empty.textContent = 'No products match this price range. Try changing or clearing the price filter.';
      grid.before(empty);
    }
    syncInputs(doc, range, all.reduce((max, product) => Math.max(max, product.price), 0));
    if (hasRange) syncLinks(doc, original, range);
    return doc.documentElement.outerHTML;
  }

  function failed(error) {
    document.querySelectorAll('.loading').forEach(element => element.classList.remove('loading'));
    document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach(element => element.classList.add('hidden'));
    let message = document.getElementById('WfsPriceFilterError');
    if (!message) {
      message = document.createElement('p');
      message.id = 'WfsPriceFilterError';
      message.setAttribute('role', 'alert');
      document.getElementById('ProductGridContainer')?.prepend(message);
    }
    message.textContent = 'Unable to update price sorting or filtering. Please try again or reload the page.';
    console.error('Unit-price filter:', error);
  }

  function initialize() {
    if (!document.getElementById('ProductGridContainer')) return;
    const url = new URL(window.location.href);
    const range = bounds(url);
    if (range.min !== null || range.max !== null || sortDirection(url)) {
      FacetFiltersForm.renderPage(window.location.search.slice(1), null, false);
    }
  }
  return { fetchSection, failed, initialize, bounds, note };
})();


class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    const requestedSearch = FacetFiltersForm.searchParamsPrev;
    WfsUnitPriceFilter.fetchSection(url)
      .then((responseText) => {
        if (requestedSearch !== FacetFiltersForm.searchParamsPrev) return;
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      }).catch((error) => {
        if (requestedSearch === FacetFiltersForm.searchParamsPrev) WfsUnitPriceFilter.failed(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    // Close all open horizontal filter <details> after AJAX rendering.
    // The disclosure-has-popup[open] CSS creates a full-screen transparent
    // ::before overlay (z-index 2) that blocks all page clicks. After the
    // summary is replaced via innerHTML/outerHTML the native <details>
    // toggle can break, leaving the overlay permanently active.
    document
      .querySelectorAll('#FacetFiltersForm .js-filter[open], #FacetFiltersPillsForm .js-filter[open]')
      .forEach((details) => details.removeAttribute('open'));

    // Re-bind aria-expanded click handlers on new summary elements
    // (mirrors the setup in global.js that only runs on initial page load).
    document.querySelectorAll('#FacetFiltersForm [id^="Details-"] summary').forEach((summary) => {
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', false);
      if (summary.nextElementSibling && summary.nextElementSibling.getAttribute('id')) {
        summary.setAttribute('aria-controls', summary.nextElementSibling.id);
      }
      summary.addEventListener('click', (event) => {
        event.currentTarget.setAttribute(
          'aria-expanded',
          !event.currentTarget.closest('details').hasAttribute('open')
        );
      });
    });
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    // Re-bind event listeners only on elements inside .js-filter that were
    // replaced by renderFilters (new summaries/buttons need handlers).
    // Avoids calling bindEvents() on the whole menu-drawer, which would
    // stack duplicate handlers on the main summary and footer buttons —
    // causing the drawer to malfunction on desktop/tablet after repeated
    // filtering. Mobile is unaffected (accordion override short-circuits).
    const menuDrawer = document.getElementById('FacetFiltersFormMobile').closest('menu-drawer');
    if (menuDrawer) {
      menuDrawer.querySelectorAll('.js-filter summary').forEach((summary) =>
        summary.addEventListener('click', menuDrawer.onSummaryClick.bind(menuDrawer))
      );
      menuDrawer
        .querySelectorAll(
          '.js-filter button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
        )
        .forEach((button) => button.addEventListener('click', menuDrawer.onCloseButtonClick.bind(menuDrawer)));
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);


if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', WfsUnitPriceFilter.initialize, { once: true });
} else {
  WfsUnitPriceFilter.initialize();
}
